import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { partsPerStockLocationCsvReportGenerator } from '../../thunks/parts_per_stock_locations_thunks';
import { equipmentTypesPerStockLocationCsvReportGenerator } from '../../thunks/equipment_types_per_stock_location_thunks';
import { equipmentPerStockLocationCsvReportGenerator } from '../../thunks/equipment_per_stock_locations_thunks';
import { Button, Dropdown, Menu } from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { shipmentsCsvReportGenerator } from '../../thunks/requested_parts_thunks';
import { downloadBackordersCSVForSupplier } from '../../thunks/backorders_thunks';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const EXPORT_TYPES = {
	PARTS_LEVEL: 'PARTS_LEVEL',
	EQUIPMENT_LEVEL: 'EQUIPMENT_LEVEL',
	STOCKED_EQUIPMENT: 'STOCKED_EQUIPMENT',
	DISBURSED_EQUIPMENT: 'DISBURSED_EQUIPMENT',
	PENDING_SHIPMENTS: 'PENDING_SHIPMENTS',
	SHIPPED: 'SHIPPED',
	BACKORDERS: 'BACKORDERS',
};

const ExportSLDetailsButton: FC<any> = ({
	exportPartsLevel,
	exportEquipmentLevel,
	exportEquipment,
	exportShipments,
	exportBackorders,
	targetCollectionName,
	stockLocations,
}): React.ReactElement => {
	const [exporting, setExporting] = useState(false);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${targetCollectionName}.${propName}`, stockLocations, {}),
		[stockLocations, targetCollectionName]
	);

	const filters = useMemo(() => {
		const slFilters = getStateSliceProp('filters');
		return Object.fromEntries(
			Object.entries(slFilters).map(([key, value]) => [`${key}StockLocation`, value])
		);
	}, [getStateSliceProp]);

	const exportRecords = useCallback(
		(type) => () => {
			setExporting(true);
			switch (type) {
				case EXPORT_TYPES.PARTS_LEVEL:
					exportPartsLevel(filters).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.EQUIPMENT_LEVEL:
					exportEquipmentLevel(filters).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.STOCKED_EQUIPMENT:
					exportEquipment({ ...filters, isDisbursed: false }).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.DISBURSED_EQUIPMENT:
					exportEquipment({ ...filters, isDisbursed: true }).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.PENDING_SHIPMENTS:
					exportShipments({ ...filters, shipped: false }).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.SHIPPED:
					exportShipments({ ...filters, shipped: true }).finally(() => setExporting(false));
					break;
				case EXPORT_TYPES.BACKORDERS:
					exportBackorders(filters).finally(() => setExporting(false));
					break;
			}
		},
		[
			exportBackorders,
			exportEquipment,
			exportEquipmentLevel,
			exportPartsLevel,
			exportShipments,
			filters,
		]
	);

	const exportMenu = useMemo(
		() => (
			<Menu>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.PARTS_LEVEL)}>Export Parts Level</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.EQUIPMENT_LEVEL)}>
					Export Equipment Level
				</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.STOCKED_EQUIPMENT)}>
					Export Stocked Equipment
				</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.DISBURSED_EQUIPMENT)}>
					Export Disbursed Equipment
				</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.PENDING_SHIPMENTS)}>
					Export Pending Shipments
				</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.SHIPPED)}>Export Shipped</Menu.Item>
				<Menu.Item onClick={exportRecords(EXPORT_TYPES.BACKORDERS)}>Export Backorders</Menu.Item>
			</Menu>
		),
		[exportRecords]
	);

	return (
		<Dropdown overlay={exportMenu}>
			<Button size="large" className="inline-block-visible-md ml-2" loading={exporting}>
				<div className="inline-block 2xl:hidden">
					<DownloadOutlined />
				</div>
				<div className="hidden 2xl:inline-block">Export</div>
				<div className="ml-2 hidden 2xl:inline-block">
					<DownOutlined />
				</div>
			</Button>
		</Dropdown>
	);
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	exportPartsLevel: (params) =>
		dispatch(partsPerStockLocationCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
	exportEquipmentLevel: (params) =>
		dispatch(equipmentTypesPerStockLocationCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
	exportEquipment: (params) =>
		dispatch(equipmentPerStockLocationCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
	exportShipments: (params) => dispatch(shipmentsCsvReportGenerator(ROLE_TYPES.SUPPLIER)(params)),
	exportBackorders: (params, filters) =>
		dispatch(downloadBackordersCSVForSupplier(params, filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExportSLDetailsButton as any);
